import { get, isEmpty } from 'lodash';

import type {
  ElementCondition,
  QuestionLibraryScreenData,
} from '../types/common';

/**
 * evaluateCondition that is used to evaluate the condition of a question
 * @param condition - The condition object that needs to be evaluated
 * @param currentFieldValues - The current field values of the whole form
 * @param screenData - The screen data of for the page
 */
export function evaluateCondition(
  condition: ElementCondition,
  currentFieldValues: Record<string, any>,
  screenData: QuestionLibraryScreenData = {},
  metadata: Record<string, unknown> = {},
) {
  if (!condition || !Object.keys(condition).length) {
    return true;
  }

  if ('or' in condition) {
    return condition.or.some((orCondition) =>
      evaluateCondition(orCondition, currentFieldValues, screenData, metadata),
    );
  }

  if ('and' in condition) {
    return condition.and.every((andCondition) => {
      return evaluateCondition(
        andCondition,
        currentFieldValues,
        screenData,
        metadata,
      );
    });
  }

  let value: unknown;

  switch (condition.dataSource) {
    case 'metadata':
      value = metadata[condition.when];
      break;
    case 'screenData':
      if (condition.when.endsWith('.isAiHidden')) {
        const questionName = condition.when.replace(/\.isAiHidden$/, '');
        value = screenData.isAiHiddenByQuestion?.[questionName] ?? false;
      } else {
        value = get(screenData, condition.when);
      }
      break;
    default:
      value = get(currentFieldValues, formatFieldName(condition.when));
      break;
  }

  return evaluate(value, condition);
}

/**
 * evaluate - evaluates the value against the condition
 * @param value - The value to be evaluated
 * @param condition - The condition to be evaluated against
 * @returns- Returns true if the value meets the condition, otherwise false
 */
const evaluate = (value: unknown, condition: ElementCondition) => {
  if ('isNot' in condition) {
    return value !== condition.isNot;
  }

  if (value === undefined) {
    return false;
  }
  if ('is' in condition) {
    return value === condition.is;
  }
  if ('isEmpty' in condition) {
    return isEmpty(value) === condition.isEmpty;
  }
  if (['gt', 'gte', 'lt', 'lte'].some((key) => key in condition)) {
    return evaluateComparisonConditions(value, condition);
  }
  return false;
};

const evaluateComparisonConditions = (
  value: unknown,
  condition: ElementCondition,
) => {
  if (value === undefined) {
    return false;
  }

  if ('gt' in condition) {
    return value > condition.gt;
  }
  if ('gte' in condition) {
    return value >= condition.gte;
  }
  if ('lt' in condition) {
    return value < condition.lt;
  }
  if ('lte' in condition) {
    return value <= condition.lte;
  }
  return false;
};

/**
 * validateField - validates a field based on the rules provided
 * @param {string} value - value to validate
 * @param {Object} rules - rules to validate against
 * @returns {Object | null} - returns an error object if the value is invalid
 */

export function validateField(value, rules) {
  let error = null;

  if (
    rules.required &&
    (value === undefined || value === null || value === '')
  ) {
    error = { type: 'required', message: 'Required' };
  }

  return error;
}

export function setCitation(citation) {
  window.postMessage(
    {
      type: 'pdfCitation',
      payload: citation,
    },
    globalThis.location.origin,
  );
}

/**
 * @description Formats the field name to be used to find errors from arrayName.index.fieldName to arrayName[index].fieldName
 * regex to replace all .index. with [index].
 * @param {string} fieldName
 * @returns {string} formattedFieldName
 */
export const formatFieldName = (fieldName) => {
  return fieldName.replaceAll(/\.(\d+)\./g, '[$1].');
};

/**
 * @description Scrolls to the question with the querySelector you supply
 * @param {string} querySelector
 */
export const scrollToQuestion = (querySelector) => {
  const element = document.querySelector(querySelector);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }
};
